import * as React from 'react';
import { graphql, PageProps } from 'gatsby';
import Img, { FluidObject } from 'gatsby-image';

import { Layout } from '../components/Layout';
import { Container } from '../components/Container';
import { SEO } from '../components/SEO';
import { Section } from '../components/Section';
import { Map } from '../components/Map';
import { Address } from '../components/Address';
import { Reviews } from '../components/Reviews';
import { GoToLink } from '../components/GoToLink';
import { FiveStarWords } from '../components/FiveStarWords';
import { CallMe } from '../components/CallMe';

import CertificateIcon from '../images/home/certificate.inline.svg';
import CreditCardIcon from '../images/home/credit-card.inline.svg';
import FriendsIcon from '../images/home/friends.inline.svg';
import HearingAidIcon from '../images/home/hearing-aid.inline.svg';
import HomeIcon from '../images/home/home.inline.svg';
import CTAArrowIcon from '../images/home/cta-arrow.inline.svg';

import config from '../config/home';
import styles from './index.module.css';

export type IndexPageProps = PageProps<GatsbyTypes.HomePageQuery>;

const IndexPage = ({ data }: IndexPageProps) => {
  const homeHeroFluid = data.homeHero?.childImageSharp?.fluid as FluidObject;
  const practionerFluid = data.practioner?.childImageSharp
    ?.fluid as FluidObject;
  const insuranceFluid = data.insurance?.childImageSharp?.fluid as FluidObject;
  const org = data.site?.siteMetadata?.organization;

  return (
    <Layout>
      <SEO title="Home" />

      {org && org.name === 'Peak View Hearing and Tinnitus' ? (
        <Container>
          <div style={{ fontStyle: 'italic', fontSize: 16 }}>
            We are excited to announce that <strong>Cornerstone Hearing is now part of
            the Connect Hearing family.</strong> By joining them, we feel that we are
            even better equipped to offer the highest levels of hearing
            healthcare and the most advanced hearing aid technologies. Though
            the name on our signs might look different, you’ll recognize the
            same professional and friendly staff. For any questions, please
            don’t hesitate to call our office at the numbers listed above. We
            look forward to supporting you on your path to better hearing.
          </div>
        </Container>
      ) : null}

      <Section>
        <Container>
          <p>{config.subHeading}</p>
          <h1>
            {config.trialPeriodWording.map((wording) => (
              <>
                {wording}
                <br />
              </>
            ))}
          </h1>

          <CallMe>
            <div className={styles.markerText}>
              <div className={styles.markerText}>
                Everyone has questions, don't be shy.
              </div>
              <CTAArrowIcon />
            </div>
          </CallMe>

          <div className={styles.fiveStarWords}>
            {config.fiveStarReviews.map((r) => (
              <FiveStarWords key={r} className={styles.fiveStarWord}>
                {r}
              </FiveStarWords>
            ))}

            <div className={styles.fiveStarWordsSubline}>
              Five-star Google reviews.
            </div>
          </div>

          <Img
            fluid={homeHeroFluid}
            alt="Our neighborhood office"
            style={{ marginBottom: '0', borderRadius: 8 }}
            loading="lazy"
          />

          <ul className={styles.takeaways}>
            <li className={styles.takeaway}>
              <div className={styles.takeawayIcon}>
                <HomeIcon />
              </div>
              <div className={styles.takeawayLabel}>
                <div>
                  {config.trialPeriodImageWording.map((wording) => (
                    <>
                      {wording}
                      <br />
                    </>
                  ))}
                </div>
              </div>
            </li>

            <li className={styles.takeaway}>
              <div className={styles.takeawayIcon}>
                <CreditCardIcon />
              </div>
              <div className={styles.takeawayLabel}>
                Simple <br />
                {config.financingRate}% financing
              </div>
            </li>

            <li className={styles.takeaway}>
              <div className={styles.takeawayIcon}>
                <FriendsIcon />
              </div>
              <div className={styles.takeawayLabel}>
                Hassle-free <br />
                returns
              </div>
            </li>

            <li className={styles.takeaway}>
              <div className={styles.takeawayIcon}>
                <HearingAidIcon />
              </div>
              <div className={styles.takeawayLabel}>
                Discreet <br />
                hearing aids
              </div>
            </li>

            <li className={styles.takeaway}>
              <div className={styles.takeawayIcon}>
                <CertificateIcon />
              </div>
              <div className={styles.takeawayLabel}>
                {config.warrantyPeriodYrs}yr wty on <br />
                nearly all devices.
              </div>
            </li>
          </ul>
        </Container>
      </Section>

      <Section altBackground>
        <Container>
          <div className={styles.appointment}>
            <div className={styles.appointmentText}>
              <h2>Your first appointment is a breeze.</h2>
              <ol>
                {config.appointmentTextBulletPoints.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ol>
            </div>

            <figure className={styles.appointmentImage}>
              <Img
                fluid={practionerFluid}
                alt={`Portrait of ${config.practionerFirstName}`}
                loading="lazy"
              />
              {config.ownerInfo && config.ownerInfo.length && (
                <>
                  <figcaption className={styles.insuranceFigCaption}>
                    {config.ownerInfo}
                  </figcaption>
                </>
              )}
            </figure>
            <div className={styles.appointmentLink}>
              <GoToLink to="/meet-the-team">Meet our team</GoToLink>
            </div>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          {/* TODO: If team is greater than one, say "our" */}
          <h2>People love our no pressure approach...</h2>

          <Reviews limit={5} ids={config.reviewIds} />

          <GoToLink to="/reviews">See more reviews</GoToLink>
        </Container>
      </Section>

      <Section altBackground>
        <Container>
          <div className={styles.insurance}>
            <div className={styles.insuranceText}>
              <h2>Not sure how much your insurance covers? We’ll find out.</h2>
              <p>
                After you make an appointment we immediately start researching
                your benefits. By the time you come in for your evaluation,
                we’ll know exactly how much your insurance covers.
              </p>
            </div>
            <figure className={styles.insuranceImage}>
              <Img
                fluid={insuranceFluid}
                alt="Janie, Medical Billing Specialist"
                loading="lazy"
              />
              <figcaption className={styles.insuranceFigCaption}>
                {config.medicalBillingSpecialistName},{' '}
                {config.medicalBillingSpecialistTitle}
              </figcaption>
            </figure>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          {typeof config.locationHeading === 'function' ? (
            config.locationHeading()
          ) : (
            <h2>{config.locationHeading}</h2>
          )}

          <div className={styles.directions}>
            {data.allContentfulLocation.edges.length <= 2 ? (
              data.allContentfulLocation.edges
                .map((e) => e.node)
                .map((l) => (
                  <div className={styles.directionsLocation}>
                    {/* TODO: Quick directions go here */}
                    <div className={styles.mapContainer}>
                      <Map
                        height={400}
                        googlePlaceId={l.googlePlaceId as string}
                      />
                    </div>
                    <Address
                      recipient={l.name as string}
                      address={
                        l.address as React.ComponentProps<
                          typeof Address
                        >['address']
                      }
                    />
                  </div>
                ))
            ) : (
              // TODO: In future make this a map with pins of all the locations
              <>
                {org?.name} has multiple locations to assist you. Use the link
                below to find a location most convenient for you.
              </>
            )}
          </div>
          {data.allContentfulLocation.edges.length > 1 ? (
            <GoToLink to="/locations">See our locations</GoToLink>
          ) : (
            <GoToLink to="/location">See our location</GoToLink>
          )}
        </Container>
      </Section>
    </Layout>
  );
};

export const query = graphql`
  query HomePage {
    homeHero: file(relativePath: { eq: "home-hero.png" }) {
      childImageSharp {
        fluid(maxWidth: 1076, toFormat: JPG) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    practioner: file(relativePath: { eq: "practitioner.png" }) {
      childImageSharp {
        fluid(maxWidth: 320, toFormat: JPG) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    insurance: file(relativePath: { eq: "insurance-team.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 320, maxHeight: 320, cropFocus: ATTENTION) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    site {
      siteMetadata {
        organization {
          name
          phoneNumber
        }
      }
    }
    allContentfulLocation(limit: 2, sort: { fields: createdAt, order: ASC }) {
      edges {
        node {
          id
          address {
            streetAddress
            state
            zipcode
            secondaryAddress
            city
          }
          googlePlaceId
          name
        }
      }
    }
  }
`;

export default IndexPage;
